import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Flex, Grid, GridItem, Text, Progress, VStack, Tag, HStack } from '@chakra-ui/react';
import pic1 from '../RR_Logo1.png';
import pic2 from '../../assets/eb.png';
import pic3 from '../../assets/CDC.png';
import pic4 from '../../assets/RRLOGO.png';
import BG2 from '../game/Clicker/Floor2.png';

const icons = Math.random() > 0.1 ? [
  { type: 'icon', value: 'K' },
  { type: 'icon', value: 'J' },
  { type: 'icon', value: 'Q' },
  { type: 'image', src: pic1 },
  { type: 'image', src: pic2 },
  { type: 'icon', value: '7️⃣' },
  { type: 'image', src: pic3 },
] : [
  { type: 'icon', value: 'K' },
  { type: 'icon', value: 'J' },
  { type: 'image', value: 'scatter', src: pic4 },
];

const MIN_BET = 10;
const MAX_BET = 500;
const STARS_TO_ADVANCE = 50;
const JACKPOT_PERCENTAGES = {
  small: 0.05,
  medium: 0.1,
  large: 0.15,
};
const PAYOUT_MULTIPLIERS = {
  [pic1]: 5,
  [pic2]: 5,
  [pic3]: 5,
};


const SlotGame: React.FC = () => {
  const generateRandomReel = () => Array(3).fill(null).map(() => icons[Math.floor(Math.random() * icons.length)]);
const generateInitialReels = () => Array(5).fill(null).map(generateRandomReel);

  const [reels, setReels] = useState(generateInitialReels());
  const [stars, setStars] = useState(0);
  const [level, setLevel] = useState(1);
  const [freeSpins, setFreeSpins] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [winnings, setWinnings] = useState(0);
  const [balance, setBalance] = useState(1000);
  const [bet, setBet] = useState(MIN_BET);
  const [winningPositions, setWinningPositions] = useState<[number, number][]>([]);
  const [jackpotSmall, setJackpotSmall] = useState(20);
  const [jackpotMedium, setJackpotMedium] = useState(500);
  const [jackpotLarge, setJackpotLarge] = useState(1000);
  const [battleOutcome, setBattleOutcome] = useState<React.ReactNode>(null);
  const [starGained, setStarGained] = useState(true);
  const spinningRef = useRef(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    let spinTimeout: NodeJS.Timeout;

    if (isSpinning) {
      spinningRef.current = true;
      interval = setInterval(() => {
        setReels(reels => reels.map(() => generateRandomReel()));
      }, 100);

      spinTimeout = setTimeout(() => {
        spinningRef.current = false;
        clearInterval(interval);

        const finalReels = Array(5).fill(null).map(() => generateRandomReel());
        setReels(finalReels);

        const { totalWinnings, starsCollected, scatterCount, jackpotWon, localFreeSpins } = calculateWinnings(finalReels);

        setWinnings(totalWinnings);
        setStars(prev => prev + starsCollected);
        setBalance(prev => prev + totalWinnings);
        setFreeSpins(prev => prev + localFreeSpins);

        if (jackpotWon) {
          const payout =
            jackpotWon === 'small' ? jackpotSmall :
            jackpotWon === 'medium' ? jackpotMedium :
            jackpotWon === 'large' ? jackpotLarge : 0;

          setBalance(prev => prev + payout);

          if (jackpotWon === 'small') setJackpotSmall(20);
          if (jackpotWon === 'medium') setJackpotMedium(500);
          if (jackpotWon === 'large') setJackpotLarge(1000);

          setBattleOutcome(
            <VStack>
              <Tag
                fontSize={'3vw'}
                color={'green'}
                animation='result-animation 4s ease-in-out'
                background='black'
                position='absolute'
                top={'50vmax'}
                zIndex='300'
                opacity='1'
                textAlign='center'
                justifyContent='center'
                padding='1vw'
              >
                {jackpotWon} Jackpot won,<br />Congrats legend!
              </Tag>
            </VStack>
          );
        }

        if (stars + starsCollected >= STARS_TO_ADVANCE) {
          setStars((stars + starsCollected) % STARS_TO_ADVANCE);
          setLevel(prev => Math.min(prev + 1, 5));
          if (level === 2) setFreeSpins(prev => prev + 10);
        }

        setIsSpinning(false);
      }, 2000);
    }

    return () => {
      clearInterval(interval);
      clearTimeout(spinTimeout);
    };
  }, [isSpinning]);

  const spinReels = () => {
    if (!isSpinning) {
      if (freeSpins > 0) {
        setFreeSpins(prev => prev - 1);
      } else if (balance >= bet) {
        setBalance(prev => prev - bet);
        setJackpotSmall(prev => prev + bet * JACKPOT_PERCENTAGES.small);
        setJackpotMedium(prev => prev + bet * JACKPOT_PERCENTAGES.medium);
        setJackpotLarge(prev => prev + bet * JACKPOT_PERCENTAGES.large);
      } else return;

      setWinnings(0);
      setWinningPositions([]);
      setBattleOutcome(null);

      setIsSpinning(true);
    }
  };

  const calculateWinnings = (finalReels: any[][]) => {
    let totalWinnings = 0;
    let starsCollected = 0;
    let scatterCount = 0;
    let jackpotWon: string | null = null;
    let localFreeSpins = 0;

    const iconMap: { [key: string]: [number, number][] } = {};
    const matchedPositions: [number, number][] = [];

    for (let col = 0; col < finalReels.length; col++) {
      for (let row = 0; row < finalReels[col].length; row++) {
        const icon = finalReels[col][row];
        const iconType = icon.value || icon.src;
        if (!iconMap[iconType]) iconMap[iconType] = [];
        iconMap[iconType].push([col, row]);
      }
    }

    for (const [iconType, positions] of Object.entries(iconMap)) {
      const colArray = positions.map(([col]) => col);
      const hitCols = Array.from(new Set(colArray)).sort((a, b) => a - b);

      if (hitCols.includes(0) && hitCols.includes(1) && hitCols.includes(2)) {
        let matchLength = 3;
        if (hitCols.includes(3)) matchLength = 4;
        if (hitCols.includes(4)) matchLength = 5;

        const matchedCols = hitCols.slice(0, matchLength);
        for (const col of matchedCols) {
          const row = positions.find(([c]) => c === col)?.[1];
          if (row !== undefined) matchedPositions.push([col, row]);
        }

        const multiplier = PAYOUT_MULTIPLIERS[iconType] || 1;
        totalWinnings += bet * multiplier * matchLength;

        if (iconType === '7️⃣') {
          if (matchLength === 3) jackpotWon = 'small';
          else if (matchLength === 4) jackpotWon = 'medium';
          else if (matchLength === 5) jackpotWon = 'large';
        }

        if (iconType === 'scatter') {
          scatterCount = positions.length;
          if (scatterCount >= 3) localFreeSpins += scatterCount * 2;
        }
      }
    }

    setWinningPositions(matchedPositions);
    return { totalWinnings, starsCollected, scatterCount, jackpotWon, localFreeSpins };
  };


  return (
    <Flex
      width="full"
      backgroundImage={BG2}
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      backgroundSize="cover"
      className="slots2"
      direction="column"
      alignItems="center"
      justifyContent="center"
      height="auto"
      color="white"
    >
      <Box borderWidth="10px" width="60%" className="slots2" textAlign="center" mb={10}>
        <Text fontSize="lg">Level: {level}</Text>
        <Progress value={(stars / STARS_TO_ADVANCE) * 100} size="md" colorScheme="yellow" />
        <Text fontSize="sm" color="yellow.500">{`${stars} / ${STARS_TO_ADVANCE} ⭐`}</Text>
        <Text fontSize="lg" color="yellow.500">{`Free Spins: ${freeSpins}`}</Text>
        <Box textAlign="center" mb={4}>
          <Text fontSize="lg" color="blue.500">Small Jackpot: {jackpotSmall.toFixed(2)}</Text>
          <Text fontSize="lg" color="orange.500">Medium Jackpot: {jackpotMedium.toFixed(2)}</Text>
          <Text fontSize="lg" color="red.500">Large Jackpot: {jackpotLarge.toFixed(2)}</Text>
        </Box>
      </Box>

      <Grid transform="scale(1.5)" className="slots2" templateColumns="repeat(5, 1fr)" gap={4}>
        {reels.map((reel, reelIndex) => (
          <GridItem
            key={reelIndex}
            w="100px"
            p="2"
            className="slots2"
            borderWidth="5px"
          >
            <Grid templateRows="repeat(3, 1fr)" gap={2}>
              {reel.map((icon, iconIndex) => {
                const isWinning = winningPositions.some(([c, r]) => c === reelIndex && r === iconIndex);
                return (
                  <GridItem
                    key={iconIndex}
                    textAlign="center"
                    boxSize="70px"
                    className={isWinning ? 'fire' : ''}
                  >
                    {icon.type === 'image' ? (
                      <img
                        src={icon.src}
                        alt="slot-icon"
                        style={{ width: '70px', height: '70px', objectFit: 'contain', background: 'transparent' }}
                      />
                    ) : (
                      <Box as="span" fontSize="xxx-large" color="white">{icon.value}</Box>
                    )}
                  </GridItem>
                );
              })}
            </Grid>
          </GridItem>
        ))}
      </Grid>

      <HStack mt={6} alignItems="center" justifyContent="center">
        <Button colorScheme="teal" onClick={() => setBet(prev => Math.max(prev - 10, MIN_BET))} disabled={bet <= MIN_BET}>-</Button>
        <Text fontSize="xl" color="yellow.500">{`Bet: ${bet}`}</Text>
        <Button colorScheme="teal" onClick={() => setBet(prev => Math.min(prev + 10, MAX_BET))} disabled={bet >= MAX_BET}>+</Button>
        <Button colorScheme="green" onClick={spinReels} disabled={isSpinning || (freeSpins <= 0 && balance < bet)}>
          {isSpinning ? 'Spinning...' : freeSpins > 0 ? 'Free Spin' : 'Spin'}
        </Button>
        <Box paddingLeft={'20px'} textAlign="center" mt={4}>
          <Text fontSize="xl" color="green.500">Winnings: {winnings}</Text>
          <Text fontSize="lg">Balance: {balance}</Text>
        </Box>
      </HStack>

      {battleOutcome}
    </Flex>
  );
};

export default SlotGame;
